import type { CheckoutProvider } from "../funnel/checkoutProvider";

export type MetaTrackingContext = {
  eventId: string;
  fbp: string | null;
  fbc: string | null;
};

export type PendingCheckoutTracking = {
  sessionId: string;
  eventId: string;
  planId: string;
  value: number;
  currency: string;
  provider?: CheckoutProvider;
  completionToken?: string;
  createdAt: number;
};

type MetaPixelPurchaseEvent = {
  eventId: string;
  contentId: string;
  currency: string;
  value: number;
};

type MetaPixelStartTrialEvent = MetaPixelPurchaseEvent;

type MetaPixelInitiateCheckoutEvent = {
  eventId: string;
  contentId: string;
  currency: string;
  value: number;
};

type MetaPixelViewContentEvent = {
  eventId: string;
  contentId: string;
  currency: string;
  value: number;
};

type FbqFunction = (
  command: "track",
  eventName: string,
  parameters?: Record<string, unknown>,
  options?: Record<string, unknown>
) => void;

type WindowWithMetaPixel = Window &
  typeof globalThis & {
    fbq?: FbqFunction;
  };

const PENDING_CHECKOUT_STORAGE_KEY = "vireo:pending-checkout";
const TRACKED_PURCHASE_EVENT_IDS_STORAGE_KEY = "vireo:tracked-purchase-events";
const MAX_PENDING_CHECKOUT_AGE_MS = 12 * 60 * 60 * 1000;

const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    // Malformed or inaccessible attribution cookies must not block checkout.
    return null;
  }
};

const generateEventId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

const getSessionStorage = (): Storage | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const getFbq = (): FbqFunction | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const { fbq } = window as WindowWithMetaPixel;
  return typeof fbq === "function" ? fbq : null;
};

const readTrackedPurchaseEventIds = (): string[] => {
  const storage = getSessionStorage();
  if (!storage) {
    return [];
  }

  const rawValue = storage.getItem(TRACKED_PURCHASE_EVENT_IDS_STORAGE_KEY);
  if (!rawValue) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(rawValue);
    return Array.isArray(parsedValue)
      ? parsedValue.filter((value): value is string => typeof value === "string" && value.trim().length > 0)
      : [];
  } catch {
    return [];
  }
};

const trackedConversionKey = (eventName: "Purchase" | "StartTrial", eventId: string): string =>
  `${eventName}:${eventId}`;

const wasConversionEventTracked = (
  eventName: "Purchase" | "StartTrial",
  eventId: string
): boolean => {
  const tracked = readTrackedPurchaseEventIds();
  return tracked.includes(trackedConversionKey(eventName, eventId)) ||
    (eventName === "Purchase" && tracked.includes(eventId));
};

const markConversionEventTracked = (
  eventName: "Purchase" | "StartTrial",
  eventId: string
): void => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  const trackedEventIds = readTrackedPurchaseEventIds();
  const key = trackedConversionKey(eventName, eventId);
  if (trackedEventIds.includes(key)) {
    return;
  }

  storage.setItem(
    TRACKED_PURCHASE_EVENT_IDS_STORAGE_KEY,
    JSON.stringify([...trackedEventIds, key])
  );
};

const readPendingCheckoutTracking = (): PendingCheckoutTracking | null => {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }

  const rawValue = storage.getItem(PENDING_CHECKOUT_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(rawValue) as Partial<PendingCheckoutTracking>;
    if (
      typeof parsedValue.sessionId !== "string" ||
      typeof parsedValue.eventId !== "string" ||
      typeof parsedValue.planId !== "string" ||
      typeof parsedValue.value !== "number" ||
      typeof parsedValue.currency !== "string" ||
      (
        parsedValue.provider !== undefined &&
        parsedValue.provider !== "stripe" &&
        parsedValue.provider !== "polar"
      ) ||
      (parsedValue.completionToken !== undefined && typeof parsedValue.completionToken !== "string") ||
      typeof parsedValue.createdAt !== "number"
    ) {
      storage.removeItem(PENDING_CHECKOUT_STORAGE_KEY);
      return null;
    }

    if (Date.now() - parsedValue.createdAt > MAX_PENDING_CHECKOUT_AGE_MS) {
      storage.removeItem(PENDING_CHECKOUT_STORAGE_KEY);
      return null;
    }

    return parsedValue as PendingCheckoutTracking;
  } catch {
    storage.removeItem(PENDING_CHECKOUT_STORAGE_KEY);
    return null;
  }
};

export const getMetaTrackingContext = (): MetaTrackingContext => ({
  eventId: generateEventId(),
  fbp: getCookie("_fbp"),
  fbc: getCookie("_fbc")
});

export const rememberPendingCheckoutTracking = (
  tracking: Omit<PendingCheckoutTracking, "createdAt">
): void => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  storage.setItem(
    PENDING_CHECKOUT_STORAGE_KEY,
    JSON.stringify({
      ...tracking,
      createdAt: Date.now()
    } satisfies PendingCheckoutTracking)
  );
};

export const getPendingCheckoutTracking = (
  expectedSessionId?: string
): PendingCheckoutTracking | null => {
  const pendingTracking = readPendingCheckoutTracking();
  if (!pendingTracking) {
    return null;
  }

  if (
    expectedSessionId &&
    pendingTracking.sessionId.trim().length > 0 &&
    pendingTracking.sessionId !== expectedSessionId
  ) {
    return null;
  }

  return pendingTracking;
};

export const trackMetaLead = (eventId?: string): void => {
  const fbq = getFbq();
  if (!fbq) {
    console.warn("[meta-pixel] Lead not fired — fbq unavailable");
    return;
  }

  const normalizedEventId = eventId?.trim();
  if (normalizedEventId) {
    console.log(`[meta-pixel] Dispatching Lead (event_id=${normalizedEventId})`);
    fbq("track", "Lead", {}, { eventID: normalizedEventId });
    return;
  }

  console.log("[meta-pixel] Dispatching Lead");
  fbq("track", "Lead");
};

export const trackMetaViewContent = ({
  eventId,
  contentId,
  currency,
  value
}: MetaPixelViewContentEvent): void => {
  const normalizedEventId = eventId.trim();
  const normalizedContentId = contentId.trim();
  const normalizedCurrency = currency.trim().toUpperCase();
  if (
    !normalizedEventId ||
    !normalizedContentId ||
    !/^[A-Z]{3}$/.test(normalizedCurrency) ||
    !Number.isFinite(value) ||
    value <= 0
  ) {
    console.warn("[meta-pixel] ViewContent not fired — invalid payload");
    return;
  }

  const fbq = getFbq();
  if (!fbq) {
    console.warn("[meta-pixel] ViewContent not fired — fbq unavailable");
    return;
  }
  console.log(`[meta-pixel] Dispatching ViewContent (event_id=${normalizedEventId})`);
  fbq(
    "track",
    "ViewContent",
    {
      content_ids: [normalizedContentId],
      content_type: "product",
      currency: normalizedCurrency,
      value
    },
    { eventID: normalizedEventId }
  );
};

export const trackMetaInitiateCheckout = ({
  eventId,
  contentId,
  currency,
  value
}: MetaPixelInitiateCheckoutEvent): void => {
  const normalizedEventId = eventId.trim();
  if (!normalizedEventId || !contentId.trim() || !currency.trim() || !Number.isFinite(value)) {
    console.warn("[meta-pixel] InitiateCheckout not fired — invalid payload");
    return;
  }

  const fbq = getFbq();
  if (!fbq) {
    console.warn(`[meta-pixel] InitiateCheckout not fired — fbq unavailable (event_id=${normalizedEventId})`);
    return;
  }

  console.log(`[meta-pixel] Dispatching InitiateCheckout (event_id=${normalizedEventId})`);
  fbq(
    "track",
    "InitiateCheckout",
    {
      content_ids: [contentId],
      content_type: "product",
      currency,
      value
    },
    {
      eventID: normalizedEventId
    }
  );
};

export const trackMetaPurchase = ({
  eventId,
  contentId,
  currency,
  value
}: MetaPixelPurchaseEvent): boolean => {
  const normalizedEventId = eventId.trim();
  if (!normalizedEventId || !contentId.trim() || !currency.trim() || !Number.isFinite(value)) {
    return false;
  }

  if (wasConversionEventTracked("Purchase", normalizedEventId)) {
    return true;
  }

  const fbq = getFbq();
  if (!fbq) {
    console.warn(`[meta-pixel] Purchase not fired — fbq unavailable (event_id=${normalizedEventId})`);
    return false;
  }

  console.log(`[meta-pixel] Dispatching Purchase (event_id=${normalizedEventId})`);
  fbq(
    "track",
    "Purchase",
    {
      content_ids: [contentId],
      content_type: "product",
      currency,
      value
    },
    {
      eventID: normalizedEventId
    }
  );

  markConversionEventTracked("Purchase", normalizedEventId);
  return true;
};

export const trackMetaStartTrial = ({
  eventId,
  contentId,
  currency
}: MetaPixelStartTrialEvent): boolean => {
  const normalizedEventId = eventId.trim();
  const normalizedContentId = contentId.trim();
  const normalizedCurrency = currency.trim().toUpperCase();
  if (!normalizedEventId || !normalizedContentId || !/^[A-Z]{3}$/.test(normalizedCurrency)) {
    return false;
  }
  if (wasConversionEventTracked("StartTrial", normalizedEventId)) {
    return true;
  }
  const fbq = getFbq();
  if (!fbq) {
    console.warn(`[meta-pixel] StartTrial not fired — fbq unavailable (event_id=${normalizedEventId})`);
    return false;
  }
  console.log(`[meta-pixel] Dispatching StartTrial (event_id=${normalizedEventId})`);
  fbq(
    "track",
    "StartTrial",
    {
      content_ids: [normalizedContentId],
      content_type: "product",
      currency: normalizedCurrency,
      value: 0
    },
    { eventID: normalizedEventId }
  );
  markConversionEventTracked("StartTrial", normalizedEventId);
  return true;
};

export const trackVerifiedCheckoutConversion = (
  conversion: null | {
    kind: "start_trial" | "purchase";
    eventId: string;
    currency: string;
    value: number;
  },
  contentId: string
): boolean => {
  if (!conversion) return false;
  const event = {
    eventId: conversion.eventId,
    contentId,
    currency: conversion.currency,
    value: conversion.value
  };
  return conversion.kind === "start_trial"
    ? trackMetaStartTrial(event)
    : trackMetaPurchase(event);
};
